import axios, { InternalAxiosRequestConfig, AxiosInstance } from 'axios';
import { tokenRefresh } from '@/shared/apis/auth/token-refresh';
import { switchTokenRefresh } from '@/shared/apis/auth/switch-token-refresh';
import { reportForcedLogoutApi } from '@/shared/apis/auth/forced-logout';
import { ROUTES } from '@/shared/constants/auth/route';
import { deleteCookie, getCookie } from '@/shared/server-actions';
import { useTokenStore } from '@/shared/store/token-store';
import { ErrorResponse } from '@/shared/types';
import { shouldAttemptTokenRefresh } from '@/shared/apis/auth/token-refresh-policy';
import { isOriginalSessionExpiredError } from '@/shared/apis/auth/account-switch-end-policy';
import { clearSwitchSession } from '@/shared/lib/account-switch-session';
import { redirectToPartnerIfHinted } from '@/shared/lib/sso-return-url-hint';

/** refresh token이 없어서 갱신이 불가능한 경우 (자연 만료) */
export class SessionExpiredError extends Error {
  constructor() {
    super('No refresh token available');
    this.name = 'SessionExpiredError';
  }
}

/** redirectToLogin 중복 호출 방지 플래그 */
let isRedirecting = false;

export const isSessionExpiredRefreshError = (error: unknown): boolean => {
  return (
    error instanceof SessionExpiredError ||
    (error instanceof Error && error.cause instanceof SessionExpiredError)
  );
};

export const shouldRedirectOnTokenRefreshFailure = (
  error: unknown,
): boolean => {
  if (isSessionExpiredRefreshError(error)) {
    return true;
  }

  if (!axios.isAxiosError<ErrorResponse>(error)) {
    return false;
  }

  return shouldAttemptTokenRefresh(
    error.response?.status,
    error.response?.data?.code,
  );
};

/**
 * 토큰 갱신 및 상태 업데이트를 위한 함수
 * - refresh token 없음 → SessionExpiredError throw (자연 만료)
 * - refresh token 있지만 갱신 실패 → 원래 에러 throw (비정상)
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  const newAccessToken = await tokenRefresh();
  return newAccessToken || null;
};

/**
 * switch 토큰 갱신 및 상태 업데이트를 위한 함수
 */
export const refreshSwitchAuthToken = async (): Promise<string | null> => {
  const newAccessToken = await switchTokenRefresh();
  return newAccessToken || null;
};

/**
 * 로그인 페이지로 리다이렉션하는 함수
 * 현재 URL을 returnUrl 파라미터로 전달하여 로그인 후 원래 페이지로 돌아갈 수 있도록 함
 */
export const redirectToLogin = async (
  axiosClient: AxiosInstance,
  logoutReason?: string,
): Promise<void> => {
  if (typeof window === 'undefined') return;

  // 이미 리다이렉트 진행 중이면 중복 실행 방지
  if (isRedirecting) return;
  isRedirecting = true;

  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  const isAlreadyOnAuthPage = currentPath.includes('/auth/');

  const navigateToSignIn = () => {
    if (isAlreadyOnAuthPage) {
      // /auth/* 직접 방문 가드 — resolve 는 비삭제이므로 이 분기와 무관하게 힌트는 보존된다
      isRedirecting = false;
      return;
    }
    if (redirectToPartnerIfHinted()) return; // 파트너 이동 중 — signin assign 금지. isRedirecting 은
    // 기존 assign 경로와 동일하게 true 잔류 (의도)
    const returnUrl = encodeURIComponent(currentPath + currentSearch);
    window.location.assign(`${ROUTES.AUTH.SIGNIN.path}?returnUrl=${returnUrl}`);
  };

  // 이미 토큰이 없으면 정상 로그아웃 진행 중 → 리포트 불필요
  const { accessToken } = useTokenStore.getState();
  if (!accessToken) {
    navigateToSignIn();
    return;
  }

  // logoutReason이 있으면 비정상 로그아웃 기록
  if (logoutReason) {
    reportForcedLogoutApi(logoutReason);
  }

  await deleteCookie('a_t');
  await deleteCookie('r_t');
  await deleteCookie('s_r_t');
  axiosClient.defaults.headers.Authorization = '';
  useTokenStore.getState().logout();

  // Desktop: Electron 세션 쿠키도 정리
  if (window.desktop?.clearSessionCookies) {
    await window.desktop.clearSessionCookies().catch(() => {});
  }

  navigateToSignIn();
};

/**
 * 토큰 갱신 및 요청 재시도 함수
 * 새로고침 시 in-memory 스토어가 초기화되므로 s_r_t 쿠키로 switch 상태를 판별하여
 * switch 모드면 operator 토큰을 먼저 갱신한 뒤 switch 토큰을 갱신하고 재시도
 */
export const handleTokenRefresh = async (
  originalConfig: InternalAxiosRequestConfig,
  axiosClient: AxiosInstance,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const { switchAccessToken } = useTokenStore.getState();
  // in-memory switchAccessToken 또는 s_r_t 쿠키로 switch 상태 판별
  const isSwitchMode = switchAccessToken ? true : !!(await getCookie('s_r_t'));

  try {
    if (isSwitchMode) {
      // switch 갱신 API가 최신 operator 토큰을 Authorization으로 전송하도록
      // 기존/legacy access token을 먼저 강제 갱신하고 store에 반영한다.
      const operatorToken = await refreshAuthToken();
      if (!operatorToken) {
        throw new Error('Failed to issue a new access token.');
      }
      useTokenStore.getState().setAccessToken(operatorToken);

      const switchToken = await refreshSwitchAuthToken();
      if (!switchToken) {
        throw new Error('Failed to refresh switch token.');
      }
      useTokenStore.getState().setSwitchAccessToken(switchToken);
    } else {
      const newAccessToken = await refreshAuthToken();
      if (!newAccessToken) {
        throw new Error('Failed to issue a new access token.');
      }
      useTokenStore.getState().setAccessToken(newAccessToken);
    }
  } catch (error) {
    if (isSwitchMode && isOriginalSessionExpiredError(error)) {
      await clearSwitchSession();
    }
    // refresh 단계의 terminal switch 정리는 switchTokenRefresh의 공통 lifecycle이 담당한다.
    console.error('Token refresh failed:', error);
    throw error;
  }

  // 원 요청 재시도는 refresh catch 바깥에서 수행한다. 여기서 발생한 business 403은
  // refresh 실패/terminal switch로 오인하지 않고 response interceptor로 그대로 보낸다.
  return axiosClient({ ...originalConfig });
};
